import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useNotifications } from 'lib/NotificationsProvider';
import Box from '@mui/material/Box';
import { ComponentType, ReactNode, useCallback, useState } from 'react';
import { UnauthorizedError } from 'lib/errors/UnauthorizedError';
import { ApolloError, ServerError } from '@apollo/client';
import CurrentUserResource from 'resources/CurrentUserResource';
import { useOrbit } from 'providers/OrbitProvider';

export function isUnauthorizedError(error: Error) { 
  return error instanceof UnauthorizedError || error instanceof ApolloError && (error.networkError as ServerError)?.statusCode === 401
}

type ErrorFallbackProps = {
  error: Error
}

function ErrorFallback({ error }: ErrorFallbackProps) {
  return (
    <Box sx={{ m: 4 }}>
      <Box>Unexpected error</Box>
      <Box>{error.toString()}</Box>
    </Box>
  )
}

export type VegaErrorBoundaryProps = {
  children: ReactNode
  showNotification?: boolean
  defaultErrorMessage?: string
  fallbackComponent?: ComponentType<FallbackProps>
}

type ReauthorizeState = null | 'pending' | 'redirecting'

export default function VegaErrorBoundary({ children, fallbackComponent, showNotification = true, defaultErrorMessage = "Something went wrong. Please try again." }: VegaErrorBoundaryProps) {
  const { addNotification } = useNotifications()
  const [reauthorizeState, setReauthorizeState] = useState<ReauthorizeState>(null)
  const { remote } = useOrbit()

  const handleError = useCallback((error: Error) => {
    if(showNotification) {
      const errorMessage = isUnauthorizedError(error) ? "Please sign in to continue." : defaultErrorMessage
      addNotification({variant: 'alert', message: errorMessage})
    }

    if(isUnauthorizedError(error) && reauthorizeState === null) {
      setReauthorizeState('pending')
      new CurrentUserResource({ remote }).promise.then(() => {
        setReauthorizeState(null)
      }).catch(error => {
        if(isUnauthorizedError(error)) {
          window.location.href = `/users/sign_in?return_to=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`
          setReauthorizeState('redirecting')
        } else {
          setReauthorizeState(null)
        }
      })
    }
  }, [addNotification, defaultErrorMessage, showNotification, reauthorizeState, remote])

  return (
    <ErrorBoundary
      FallbackComponent={fallbackComponent || ErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  )
}
